import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { addDays } from "date-fns";
import { useAgendamentoPorId } from "../../../hooks/queries/agendamento/useAgendamentoPorId/useAgendamentoPorId";
import { useDefinirHorario } from "../../../hooks/queries/agendamento/useDefinirHorario/useDefinirHorario";
import { useReagendar } from "../../../hooks/queries/agendamento/useReagendar/useReagendar";
import { useLojaDiasIndisponiveis } from "../../../hooks/queries/loja/useLojaDiasIndisponiveis/useLojaDiasIndisponiveis";
import { useLojaHorariosDisponiveis } from "../../../hooks/queries/loja/useLojaHorariosDisponiveis/useLojaHorariosDisponiveis";
import { useDeliveryDiasIndisponiveis } from "../../../hooks/queries/delivery/useDeliveryDiasIndisponiveis/useDeliveryDiasIndisponiveis";
import { useDeliveryHorariosDisponiveis } from "../../../hooks/queries/delivery/useDeliveryHorariosDisponiveis/useDeliveryHorariosDisponiveis";
import { useItineranteHorariosDisponiveis } from "../../../hooks/queries/itinerante/useItineranteHorariosDisponiveis/useItineranteHorariosDisponiveis";
import { useItinerantePorId } from "../../../hooks/queries/itinerante/useItinerantePorId/useItinerantePorId";
import { useAgendamentoStepIndicator } from "../../../hooks/useAgendamentoStepIndicator";
import {
  formatDateToISO,
  getMaxSelectableDate,
  reverseToDateObject,
} from "../../../utils/dateTransform";
import { Tracking } from "../../../services/Tracking";

export const useScheduleConfirmationV2 = () => {
  const { uuidAgendamento } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const itineranteId = searchParams.get("itineranteId");
  const isReagendamento = !!searchParams.get("reagendamento");
  const stepIndicator = useAgendamentoStepIndicator();

  const [date, setDate] = useState<Date | null>(null);
  const [horaAgendada, setHoraAgendada] = useState<string | null>(null);
  const [isReagendamentoModalOpen, setIsReagendamentoModalOpen] =
    useState(false);

  const { data: agendamento } = useAgendamentoPorId(uuidAgendamento ?? "");
  const { data: itineranteSelecionado } = useItinerantePorId(
    itineranteId ?? "",
  );

  const isItinerante = !!agendamento?.itinerante?.uuid;
  const uuidLoja = !isItinerante ? (agendamento?.loja?.uuid ?? "") : "";
  const uuidDelivery = !isItinerante ? (agendamento?.delivery?.uuid ?? "") : "";
  const uuidItineranteAtivo =
    itineranteId ?? agendamento?.itinerante?.uuid ?? "";

  const { data: diasIndisponiveisLoja } = useLojaDiasIndisponiveis(uuidLoja);
  const { data: diasIndisponiveisDelivery } = useDeliveryDiasIndisponiveis(
    uuidDelivery,
    agendamento?.concessionaria,
  );
  const diasIndisponiveis = (
    diasIndisponiveisLoja ??
    diasIndisponiveisDelivery ??
    []
  ).map((dia) => addDays(new Date(dia), 1));

  const maxDate = getMaxSelectableDate(
    agendamento?.revistoria,
    agendamento?.dataLimiteRevistoria,
  );

  const dataISO = date ? formatDateToISO(date) : "";
  const { data: horariosLoja } = useLojaHorariosDisponiveis(uuidLoja, dataISO);
  const { data: horariosDelivery } = useDeliveryHorariosDisponiveis(
    uuidDelivery,
    dataISO,
  );
  const { data: horariosItinerante } = useItineranteHorariosDisponiveis(
    date ? uuidItineranteAtivo : "",
  );
  const horarios = (
    horariosLoja ??
    horariosDelivery ??
    horariosItinerante ??
    []
  ).map((horario) => ({ value: horario, label: horario }));

  const { mutate: definirHorario, isPending: isDefinindoHorario } =
    useDefinirHorario();
  const { mutate: reagendar, isPending: isReagendando } = useReagendar();

  useEffect(() => {
    if (itineranteSelecionado?.dataRealizacao) {
      setDate(reverseToDateObject(itineranteSelecionado.dataRealizacao));
      return;
    }

    if (isItinerante && agendamento?.itinerante?.dataRealizacao) {
      setDate(reverseToDateObject(agendamento.itinerante.dataRealizacao));
    }
  }, [
    itineranteSelecionado?.dataRealizacao,
    isItinerante,
    agendamento?.itinerante?.dataRealizacao,
  ]);

  function handleDateChange(novaData: Date) {
    setDate(novaData);
    setHoraAgendada(null);
  }

  function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();

    if (!date || !horaAgendada || !uuidAgendamento) return;

    if (isReagendamento) {
      setIsReagendamentoModalOpen(true);
      return;
    }

    Tracking.userAction(
      "mapa:agendamento/confirmar-horario:clique-botao-avancar",
    );

    definirHorario(
      {
        uuid: uuidAgendamento,
        diaAgendado: formatDateToISO(date),
        horaAgendada,
      },
      {
        onSuccess: ({ data }) => {
          navigate(`/agendamento/${data.uuid}/confirmar-agendamento`);
        },
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error(
            "mapa:agendamento/confirmar-horario:definir-horario-falhou",
            {
              mensagem,
            },
          );
        },
      },
    );
  }

  function handleConfirmarReagendamento() {
    if (!uuidAgendamento) return;

    Tracking.userAction(
      "mapa:agendamento/confirmar-horario:clique-confirmar-reagendamento",
    );

    reagendar(
      {
        uuidAgendamento,
        uuidLoja: agendamento?.loja?.uuid,
        uuidDelivery: agendamento?.delivery?.uuid,
        uuidItinerante: itineranteId,
        diaAgendado: isItinerante
          ? agendamento?.itinerante?.dataRealizacao
          : date
            ? formatDateToISO(date)
            : agendamento?.diaAgendado,
        horaAgendada,
      } as any,
      {
        onSuccess: ({ data }) => {
          navigate(`/meus-agendamentos/agendamento?id=${data.uuid}`);
        },
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error(
            "mapa:agendamento/confirmar-horario:reagendar-falhou",
            {
              mensagem,
            },
          );
        },
        onSettled: () => setIsReagendamentoModalOpen(false),
      },
    );
  }

  return {
    stepIndicator,
    diasIndisponiveis,
    maxDate,
    date,
    handleDateChange,
    horarios,
    horaAgendada,
    setHoraAgendada,
    handleSubmit,
    podeSubmeter: !!date && !!horaAgendada,
    isSubmitting: isDefinindoHorario || isReagendando,
    isDateLocked: !!itineranteId,
    isReagendamento,
    isReagendamentoModalOpen,
    setIsReagendamentoModalOpen,
    handleConfirmarReagendamento,
  };
};
